export type CookieConsentChoice = 'necessary' | 'all'

export const COOKIE_CONSENT_CHANGE_EVENT = 'cookie-consent-change'

export function getConsentStorageKey(): string {
  const explicit = process.env.NEXT_PUBLIC_COOKIE_CONSENT_KEY?.trim()
  if (explicit) return explicit

  const domain = String(process.env.NEXT_PUBLIC_SITE_DOMAIN || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/[^a-z0-9]+/g, '_')

  if (domain.includes('apimstec')) return 'apimstec_cookie_consent'
  if (domain.includes('airestro')) return 'airestro_cookie_consent'

  return `${domain || 'site'}_cookie_consent`
}

export function setCookieConsent(value: CookieConsentChoice): void {
  const key = getConsentStorageKey()
  try {
    localStorage.setItem(key, value)
  } catch {
    /* private mode */
  }
  try {
    sessionStorage.setItem(key, value)
  } catch {
    /* private mode */
  }
  try {
    const secure =
      typeof window !== 'undefined' && window.location.protocol === 'https:' ? ';Secure' : ''
    document.cookie = `${key}=${value};path=/;max-age=31536000;SameSite=Lax${secure}`
  } catch {
    /* blocked */
  }
  applyConsentToDocument(value)
}

export function getCookieConsent(): CookieConsentChoice | null {
  const key = getConsentStorageKey()
  try {
    const fromLs = localStorage.getItem(key)
    if (fromLs === 'necessary' || fromLs === 'all') return fromLs
    const fromSs = sessionStorage.getItem(key)
    if (fromSs === 'necessary' || fromSs === 'all') return fromSs
    const match = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${key}=`))
    if (!match) return null
    const value = match.slice(key.length + 1)
    if (value === 'necessary' || value === 'all') return value
    return null
  } catch {
    return null
  }
}

export function hasCookieConsentChoice(): boolean {
  return getCookieConsent() !== null
}

export function allowAnalyticsCookies(): boolean {
  return getCookieConsent() === 'all'
}

export function applyConsentToDocument(choice: CookieConsentChoice | null): void {
  if (typeof document === 'undefined') return
  if (choice) {
    document.documentElement.dataset.cookieConsent = choice
  } else {
    delete document.documentElement.dataset.cookieConsent
  }
}

export function getGaMeasurementId(): string | null {
  const id =
    process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID?.trim() ||
    process.env.NEXT_PUBLIC_GA_ID?.trim() ||
    process.env.NEXT_PUBLIC_VITE_GA_MEASUREMENT_ID?.trim() ||
    ''
  return id || null
}
